import { useLayoutEffect, useRef, useState } from "react";
import DestructiveConfirmationDialog from "../components/DestructiveConfirmationDialog";
import { useNativeWebMCP } from "./nativeWebMCP";
import { applicationSchemas } from "./applicationSchemas";
import { toolDescriptions } from "./toolDocumentation";
import {
  ApplicationReceipts,
  ApplicationToolError,
  applicationError,
  listApplicationDrawings,
  objectInput,
  parseApplicationRequest,
  type ApplicationRequest,
  type ApplicationResult,
} from "./applicationTools";

interface ApplicationOptions {
  accountId: string;
  enabled: boolean;
  available: boolean;
  unavailableMessage?: string;
  context?: {
    storage: "local" | "cloud";
    drawingId?: string;
    scene: "loading" | "error" | "ready";
    editable: boolean;
    dirty: boolean;
    saving: boolean;
    saveConflict: boolean;
    session: "none" | "local" | "cloud";
  };
  canvasKey: string;
  title: string;
  getToken: () => Promise<string>;
  check: (request: ApplicationRequest) => void;
  execute: (request: ApplicationRequest) => Promise<ApplicationResult>;
  shareState: () => Promise<unknown>;
}

const receipts = new ApplicationReceipts();

/** Application actions require a trusted user gesture, never an agent confirmation flag. */
export function useApplicationWebMCP(options: ApplicationOptions) {
  const current = useRef(options);
  const alive = useRef(false);
  const lifetime = useRef(0);
  const pendingRef = useRef<ApplicationRequest | null>(null);
  const executingRef = useRef(false);
  const [pending, setPending] = useState<ApplicationRequest | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const description = (tool: keyof typeof applicationSchemas) =>
    `${toolDescriptions[tool]}\nCurrent context (advisory; rechecked at execution/approval): ${JSON.stringify(
      {
        backendAvailable: options.available,
        ...options.context,
      },
    )}`;
  useLayoutEffect(() => {
    current.current = options;
  });
  useLayoutEffect(() => {
    receipts.setAccount(options.accountId);
    const epoch = lifetime.current;
    alive.current = true;
    setPending(null);
    setBusy(false);
    setError(null);
    return () => {
      alive.current = false;
      lifetime.current = epoch + 1;
      if (pendingRef.current) {
        receipts.finish(pendingRef.current, {
          status: "cancelled",
          message:
            "The editor or account changed. Submit a new request after checking the current drawing.",
        });
        pendingRef.current = null;
      }
    };
  }, [options.accountId, options.canvasKey, options.enabled, options.available]);

  const requireAvailable = () => {
    if (!alive.current || !current.current.enabled || !current.current.accountId) {
      throw new ApplicationToolError(
        "ACCESS_DENIED",
        "Cloud application tools are unavailable for this account or page.",
      );
    }
    if (!current.current.available) {
      throw new ApplicationToolError(
        "APPLICATION_UNAVAILABLE",
        current.current.unavailableMessage ??
          "Cloud and collaboration access is not ready. Check the app's access notice, sign in or request access if needed, and retry when available. A local drawing does not need to be saved to discover cloud files.",
      );
    }
  };
  const request = async (
    tool: ApplicationRequest["tool"],
    input: unknown,
    signal?: AbortSignal,
  ) => {
    try {
      signal?.throwIfAborted();
      requireAvailable();
      const parsed = parseApplicationRequest(tool, input);
      const previous = receipts.get(parsed);
      if (previous) return JSON.stringify(previous);
      if (pendingRef.current || executingRef.current)
        throw new ApplicationToolError("BUSY", "Resolve the current application action first.");
      current.current.check(parsed);
      receipts.start(parsed);
      pendingRef.current = parsed;
      signal?.addEventListener(
        "abort",
        () => {
          if (pendingRef.current === parsed) {
            receipts.finish(parsed, {
              status: "cancelled",
              message: "The client cancelled the requested action.",
            });
            pendingRef.current = null;
            if (alive.current) setPending(null);
          }
        },
        { once: true },
      );
      setPending(parsed);
      setError(null);
      return JSON.stringify(receipts.get(parsed));
    } catch (failure) {
      return JSON.stringify(applicationError(failure));
    }
  };
  useNativeWebMCP({
    name: "drawing_list",
    enabled: options.enabled,
    readOnly: true,
    description: description("drawing_list"),
    inputSchema: applicationSchemas.drawing_list,
    handler: async (input: Record<string, unknown>) => {
      try {
        requireAvailable();
        const scope = receipts.getScope();
        const result = await listApplicationDrawings(input, current.current.getToken);
        requireAvailable();
        if (scope !== receipts.getScope())
          throw new ApplicationToolError("ACCESS_DENIED", "Account changed.");
        return JSON.stringify(result);
      } catch (failure) {
        return JSON.stringify(applicationError(failure));
      }
    },
  });
  useNativeWebMCP({
    name: "drawing_open",
    enabled: options.enabled,
    description: description("drawing_open"),
    inputSchema: applicationSchemas.drawing_open,
    handler: (input: Record<string, unknown>, signal?: AbortSignal) =>
      request("drawing_open", input, signal),
  });
  useNativeWebMCP({
    name: "drawing_save",
    enabled: options.enabled,
    description: description("drawing_save"),
    inputSchema: applicationSchemas.drawing_save,
    handler: (input: Record<string, unknown>, signal?: AbortSignal) =>
      request("drawing_save", input, signal),
  });
  useNativeWebMCP({
    name: "drawing_share",
    enabled: options.enabled,
    description: description("drawing_share"),
    inputSchema: applicationSchemas.drawing_share,
    handler: async (input: Record<string, unknown>, signal?: AbortSignal) => {
      if (input.action !== "read") return request("drawing_share", input, signal);
      try {
        objectInput(input, ["action"]);
        requireAvailable();
        const epoch = lifetime.current;
        const result = await current.current.shareState();
        requireAvailable();
        if (epoch !== lifetime.current)
          throw new ApplicationToolError(
            "STALE_CANVAS",
            "Drawing or account changed. Read the current drawing.",
          );
        return JSON.stringify(result);
      } catch (failure) {
        return JSON.stringify(applicationError(failure));
      }
    },
  });

  const close = () => {
    if (busy || !pendingRef.current) return;
    receipts.finish(pendingRef.current, {
      status: "cancelled",
      message: "The user cancelled the requested action.",
    });
    pendingRef.current = null;
    setPending(null);
  };
  const approve = async () => {
    const action = pendingRef.current;
    if (!action || executingRef.current) return;
    executingRef.current = true;
    const scope = receipts.getScope();
    const epoch = lifetime.current;
    setBusy(true);
    setError(null);
    try {
      requireAvailable();
      current.current.check(action);
      receipts.finish(action, {
        status: "pending",
        message: "Approved by the user; operation is running.",
      });
      // Navigation may unmount this hook. The approved operation owns its receipt.
      pendingRef.current = null;
      const result = await current.current.execute(action);
      if (receipts.getScope() === scope) receipts.finish(action, result);
      if (alive.current && epoch === lifetime.current) {
        setPending(null);
        if (
          result.status === "error" ||
          result.status === "not_saved" ||
          result.status === "saved_snapshot"
        ) {
          setError(result.message ?? "The operation did not complete.");
        }
      }
    } catch (failure) {
      const result = applicationError(failure);
      if (pendingRef.current === action) pendingRef.current = null;
      if (receipts.getScope() === scope) receipts.finish(action, result);
      if (alive.current && epoch === lifetime.current) {
        setError(result.message ?? "The operation failed.");
        setPending(null);
      }
    } finally {
      executingRef.current = false;
      if (alive.current && epoch === lifetime.current) setBusy(false);
    }
  };
  const actionText =
    pending?.tool === "drawing_open"
      ? `Open drawing ${pending.drawingId}. Unsaved work will not be discarded.`
      : pending?.tool === "drawing_save"
        ? `Save ${pending.name ? `"${pending.name}" to cloud storage` : `"${options.title}"`}.`
        : pending?.tool === "drawing_share" && pending.action === "start_session"
          ? `Start or reuse a live session for "${options.title}" and provide a ${pending.role === "viewer" ? "view-only" : "can-edit"} temporary invitation. The canvas will be shared live; no cloud file is created and no message is sent.`
          : `Create or reuse a ${pending?.tool === "drawing_share" && pending.role === "viewer" ? "view-only" : "can-edit"} people link for "${options.title}". This changes access; no message is sent.`;
  return options.enabled && (pending || error) ? (
    <DestructiveConfirmationDialog
      title={error ? "Assistant action failed" : "Approve assistant action"}
      confirmLabel={error ? "Dismiss" : "Approve"}
      busyLabel="Working..."
      busy={busy}
      error={error}
      neutralConfirm
      testId="webmcp-application-confirmation"
      onConfirm={error ? () => setError(null) : () => void approve()}
      onClose={error ? () => setError(null) : close}
    >
      {error ? "Review the app state before asking the assistant to try again." : actionText}
    </DestructiveConfirmationDialog>
  ) : null;
}
